import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

// Issue #80's left-side drawer -- one open/close boolean, lifted to
// (app)/_layout.tsx (the one ancestor every screen in the authenticated
// shell already renders under) so any screen can open it via a
// hamburger button (MenuButton) without prop-drilling through the Tabs
// navigator. Deliberately a plain Context, not a nav-library construct
// (React Navigation's own Drawer navigator would need moving every
// route file under a nested (tabs) group -- a much bigger, riskier
// restructure than this feature needs; see AppDrawer's own doc comment
// for the full reasoning).
type AppDrawerContextValue = {
  open: boolean;
  toggle: () => void;
  close: () => void;
};

const AppDrawerContext = createContext<AppDrawerContextValue | null>(null);

export function AppDrawerProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const value = useMemo<AppDrawerContextValue>(
    () => ({ open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) }),
    [open],
  );

  return <AppDrawerContext.Provider value={value}>{children}</AppDrawerContext.Provider>;
}

export function useAppDrawer(): AppDrawerContextValue {
  const ctx = useContext(AppDrawerContext);
  if (!ctx) {
    throw new Error('useAppDrawer must be used within AppDrawerProvider (see (app)/_layout.tsx)');
  }

  return ctx;
}
